import React, { useState, useEffect } from 'react';
import { obtenerTodasLasMatriculas } from '../../services/matriculaService';
import { crearActividad } from '../../services/ActividadesService';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import moment from 'moment';
import { FaCalendarAlt, FaSearch } from 'react-icons/fa';

// Funciones de notificación estandarizadas
const showSuccessToast = (message) => {
    toast.success(message, {
        position: 'top-right',
        autoClose: 3000,
        style: { marginTop: '10px' }
    });
};

const showErrorToast = (message) => {
    toast.error(message, {
        position: 'top-right',
        autoClose: 3000,
        style: { marginTop: '10px' }
    });
};

const showWarningToast = (message) => {
    toast.warning(message, {
        position: 'top-right',
        autoClose: 3000,
        style: { marginTop: '10px' }
    });
};

const CrearActividades = ({ onClose }) => {
    const [ninos, setNinos] = useState([]);
    const [ninosFiltrados, setNinosFiltrados] = useState([]);
    const [busqueda, setBusqueda] = useState('');
    const [diasSeleccionados, setDiasSeleccionados] = useState([]);
    const [fechaError, setFechaError] = useState('');
    const [nombreError, setNombreError] = useState('');
    const [participantesError, setParticipantesError] = useState('');
    const [formData, setFormData] = useState({
        nombre: '',
        descripcion: '',
        ninosSeleccionados: [],
        fecha: moment().format('YYYY-MM-DD')
    });

    useEffect(() => {
        cargarNinos();
    }, []);

    const cargarNinos = async () => {
        try {
            const response = await obtenerTodasLasMatriculas();
            // Filtrar solo niños con matrícula activa
            const ninosActivos = response.matriculas
                .filter(matricula => matricula.matricula && matricula.matricula.estado === 'Activa')
                .map(matricula => ({
                    id_child: matricula.nino.id_child,
                    nombre: `${matricula.nino.nombre} ${matricula.nino.apellidos}`
                }));
            setNinos(ninosActivos);
            setNinosFiltrados(ninosActivos);
        } catch (error) {
            console.error('Error al cargar niños:', error);
        }
    };

    useEffect(() => {
        const filtrarNinos = () => {
            if (busqueda.trim() === '') {
                setNinosFiltrados(ninos);
            } else {
                setNinosFiltrados(
                    ninos.filter((nino) =>
                        nino.nombre.toLowerCase().includes(busqueda.toLowerCase())
                    )
                );
            }
        };

        filtrarNinos();
    }, [busqueda, ninos]);

    const handleDiaChange = (dia) => {
        if (diasSeleccionados.includes(dia)) {
        setDiasSeleccionados(diasSeleccionados.filter(d => d !== dia));
        } else {
        setDiasSeleccionados([...diasSeleccionados, dia]);
        }
    };

    const handleFechaChange = (e) => {
        const nuevaFecha = e.target.value;
        setFormData({...formData, fecha: nuevaFecha});
        
        const fechaSeleccionada = moment(nuevaFecha);
        const fechaActual = moment().startOf('day');

        if (fechaSeleccionada.isBefore(fechaActual)) {
            setFechaError('La fecha no puede ser anterior a la fecha actual');
        } else {
            setFechaError('');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const userId = JSON.parse(localStorage.getItem('user')).id;
        let hasError = false;

        if (!formData.nombre.trim()) {
            setNombreError('El nombre de la actividad es requerido');
            hasError = true;
        } else {
            setNombreError('');
        }

        if (formData.ninosSeleccionados.length === 0) {
            setParticipantesError('Por favor, seleccione al menos un participante');
            hasError = true;
        } else {
            setParticipantesError('');
        }

        const fechaInicio = moment(formData.fecha);
        const fechaActual = moment().startOf('day');

        if (fechaInicio.isBefore(fechaActual)) {
            setFechaError('La fecha de inicio no puede ser anterior a la fecha actual');
            hasError = true;
        }

        if (hasError) return;

        try {
            for (const idChild of formData.ninosSeleccionados) {
                if (diasSeleccionados.length > 0) {
                    for (const dia of diasSeleccionados) {
                        let fecha = moment(fechaInicio);
                        while (fecha.day() !== dia) {
                            fecha.add(1, 'days');
                        }
                        
                        const actividadData = {
                            nombre: formData.nombre,
                            descripcion: formData.descripcion,
                            id_profesor: userId,
                            id_child: idChild,
                            fecha_programada: fecha.format('YYYY-MM-DD')
                        };

                        await crearActividad(actividadData);
                    }
                } else {
                    const actividadData = {
                        nombre: formData.nombre,
                        descripcion: formData.descripcion,
                        id_profesor: userId,
                        id_child: idChild,
                        fecha_programada: fechaInicio.format('YYYY-MM-DD')
                    };

                    await crearActividad(actividadData);
                }
            }

            showSuccessToast('Actividad creada exitosamente');
            setFormData({
                nombre: '',
                descripcion: '',
                ninosSeleccionados: [],
                fecha: moment().format('YYYY-MM-DD')
            });
            setDiasSeleccionados([]);
            onClose();
        } catch (error) {
            showErrorToast('Error al crear la actividad. Por favor, inténtelo de nuevo.');
            console.error('Error al crear actividad:', error);
        }
    };

    return (
        <div className="bg-white rounded-lg w-full max-w-[95%] md:max-w-[600px] max-h-[90vh] overflow-y-auto shadow-xl">
            <div className="sticky top-0 bg-white p-4 md:p-6 border-b">
                <div className="flex justify-between items-center">
                    <h2 className="text-xl md:text-2xl font-bold text-gray-800">Nueva Actividad</h2>
                    <button 
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700"
                    >
                        ✕
                    </button>
                </div>
            </div>
            
            <form onSubmit={handleSubmit} className="p-4 md:p-6 space-y-4 md:space-y-6">
                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Nombre de la Actividad:</label>
                    <input
                        type="text"
                        value={formData.nombre}
                        onChange={(e) => {
                            setFormData({...formData, nombre: e.target.value});
                            setNombreError('');
                        }}
                        className={`w-full px-3 py-2 bg-white border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                            nombreError ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="Ingrese el nombre de la actividad"
                        required
                    />
                    {nombreError && (
                        <p className="text-red-500 text-sm mt-1">{nombreError}</p>
                    )}
                </div>

                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Descripción Detallada:</label>
                    <textarea
                        value={formData.descripcion}
                        onChange={(e) => setFormData({...formData, descripcion: e.target.value})}
                        className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        rows="3"
                        placeholder="Describa los detalles de la actividad"
                    />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Participantes:</label>
                        <div className="mb-2">
                            <div className="relative">
                                <input
                                    type="text"
                                    value={busqueda}
                                    onChange={(e) => setBusqueda(e.target.value)}
                                    placeholder="Buscar estudiante..."
                                    className="w-full px-3 py-2 pl-10 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                />
                                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            </div>
                        </div>
                        <div className={`border rounded-lg p-3 max-h-40 md:max-h-48 overflow-y-auto ${
                            participantesError ? 'border-red-500' : 'border-gray-300'
                        }`}>
                            {ninosFiltrados.map(nino => (
                                <label key={nino.id_child} className="flex items-center space-x-3 py-1.5 px-2 hover:bg-gray-50 rounded cursor-pointer">
                                    <div className="relative">
                                        <input
                                            type="checkbox"
                                            checked={formData.ninosSeleccionados.includes(nino.id_child)}
                                            onChange={(e) => {
                                                const newSelected = e.target.checked 
                                                    ? [...formData.ninosSeleccionados, nino.id_child]
                                                    : formData.ninosSeleccionados.filter(id => id !== nino.id_child);
                                                setFormData({...formData, ninosSeleccionados: newSelected});
                                                setParticipantesError('');
                                            }}
                                            className="sr-only peer"
                                        />
                                        <div className="w-5 h-5 border-2 border-gray-300 rounded transition-all peer-checked:bg-indigo-600 peer-checked:border-indigo-600 flex items-center justify-center">
                                            <span className="text-white peer-checked:block">✓</span>
                                        </div>
                                    </div>
                                    <span className="text-sm text-gray-700">{nino.nombre}</span>
                                </label>
                            ))}
                            {ninosFiltrados.length === 0 && (
                                <p className="text-gray-500 text-sm text-center py-2">
                                    No se encontraron estudiantes
                                </p>
                            )}
                        </div>
                        {participantesError && (
                            <p className="text-red-500 text-sm mt-1">{participantesError}</p>
                        )}
                    </div>

                    <div>
                        <div className="mb-4">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Fecha de la Actividad:</label>
                            <div className="relative">
                                <input
                                    type="date"
                                    value={formData.fecha}
                                    onChange={handleFechaChange}
                                    min={moment().format('YYYY-MM-DD')}
                                    className={`w-full px-3 py-2 bg-white border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent pl-10 ${
                                        fechaError ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                    required
                                />
                                <FaCalendarAlt className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            </div>
                            {fechaError && (
                                <p className="text-red-500 text-sm mt-1">{fechaError}</p>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Días de Repetición (Opcional):</label>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                {[
                                    {dia: 1, nombre: 'Lunes'},
                                    {dia: 2, nombre: 'Martes'},
                                    {dia: 3, nombre: 'Miércoles'},
                                    {dia: 4, nombre: 'Jueves'},
                                    {dia: 5, nombre: 'Viernes'},
                                    {dia: 6, nombre: 'Sábado'},
                                    {dia: 0, nombre: 'Domingo'}
                                ].map(({dia, nombre}) => (
                                    <label key={dia} className={`
                                        flex items-center justify-center p-2 rounded-lg cursor-pointer text-center
                                        ${diasSeleccionados.includes(dia) 
                                            ? 'bg-indigo-100 text-indigo-700 border-2 border-indigo-500' 
                                            : 'bg-gray-50 text-gray-700 border border-gray-300 hover:bg-gray-100'}
                                    `}>
                                        <input
                                            type="checkbox"
                                            checked={diasSeleccionados.includes(dia)}
                                            onChange={() => handleDiaChange(dia)}
                                            className="sr-only"
                                        />
                                        <span className="text-xs md:text-sm font-medium">{nombre}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4 md:pt-6 border-t mt-4 md:mt-6">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 md:px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-200 font-medium text-sm"
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="px-4 md:px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors duration-200 font-medium text-sm"
                    >
                        Guardar Actividad
                    </button>
                </div>
            </form>
        </div>
    );
};

export default CrearActividades;
